/**
 * Wire values of the backend's document indexing statuses, and the predicates
 * that read them.
 *
 * This module exists because BE-11's first cut did not have it. The empty-
 * conversion status landed in three components — `DocumentList`,
 * `DocumentViewerModal`, `IndexManager` — as FOUR independent spellings of one
 * backend constant in TWO casings, next to an `export { CONVERSION_EMPTY_STATUS }`
 * that no file imported. Stage 6c found it by grepping for the reader rather than
 * the declaration, which is the only way this class is ever found: TypeScript
 * cannot see that three string literals are supposed to be the same string.
 *
 * The casing is the trap, so `isConversionEmpty` owns it in one place. The wire
 * value is camelCase (`internal/document/models.go`'s `StatusConversionEmpty`),
 * but several call sites compare a `.toLowerCase()`d subject — and a case-exact
 * comparison against a lowercased subject does not fail loudly, it falls to a
 * default branch and renders a grey "unknown" badge. Design §Risks 1 names that
 * degraded state as worse than the bug this feature fixes.
 */

/**
 * Wire value of the backend's `StatusConversionEmpty`: a binary document whose
 * conversion produced no text at all.
 *
 * Terminal, and deliberately NOT `failed` — nothing errored, so a destructive
 * tone would overstate it — but equally not `indexed`, because there is nothing
 * for retrieval to find.
 */
export const CONVERSION_EMPTY_STATUS = 'conversionEmpty';

/** Wire value of the backend's `StatusFailed`. */
export const FAILED_STATUS = 'failed';

/**
 * True when the status is the empty-conversion terminal state.
 *
 * Case-insensitive on purpose: this is the single place allowed to know that the
 * wire value is camelCase, so no caller has to remember which casing its own
 * subject is in.
 */
export function isConversionEmpty(status: string | undefined | null): boolean {
  return (status ?? '').toLowerCase() === CONVERSION_EMPTY_STATUS.toLowerCase();
}

/** True when the status is the hard-failure terminal state. */
export function isFailed(status: string | undefined | null): boolean {
  return (status ?? '').toLowerCase() === FAILED_STATUS;
}

/**
 * True for the statuses that carry an actionable reason and a retry affordance.
 *
 * Both terminal states qualify: `failed` because something errored, and
 * `conversionEmpty` because a re-run against a different converter (or the same
 * one after an OCR fix ships) can genuinely produce text where there was none.
 */
export function isActionableFailure(status: string | undefined | null): boolean {
  return isFailed(status) || isConversionEmpty(status);
}

/**
 * Metadata key the backend sets on a document whose PAID indexing legs
 * (embeddings, cascade extraction) were skipped — today only for lack of credits,
 * value `"credits"`. Writer: `metaIndexSkipped` in
 * `internal/document/index_spend.go` (BILL-06); a successful full re-index
 * deletes it.
 *
 * Such a document is `indexed` (BM25 keyword search works), so
 * `isActionableFailure` does not admit it — this key is the only signal that it
 * is worth retrying once the user has credits again.
 */
const INDEX_SKIPPED_METADATA_KEY = 'index_skipped';

/**
 * True when the document's paid indexing legs were skipped. Any non-empty
 * reason counts, not only `"credits"`, so a future reason still surfaces Retry.
 */
function isIndexSkipped(
  metadata: Record<string, string> | undefined | null,
): boolean {
  return (metadata?.[INDEX_SKIPPED_METADATA_KEY] ?? '') !== '';
}

/** Wire value of the backend's `StatusIndexed`. */
const INDEXED_STATUS = 'indexed';

/**
 * True when an index-skipped document should offer Retry and the needs-credits
 * hint: its paid legs were skipped AND it is settled at `indexed`.
 *
 * The status arm is load-bearing. `RearmForRetry` flips the row to `pending`
 * but leaves `index_skipped` set until the re-run finishes, so a
 * metadata-only check kept Retry clickable for the whole re-run — and every
 * extra click spent a re-run token and started another concurrent `Process`
 * on the same document (BILL-06 Stage 6).
 */
export function offersIndexSkippedRetry(
  status: string | undefined | null,
  metadata: Record<string, string> | undefined | null,
): boolean {
  return (status ?? '').toLowerCase() === INDEXED_STATUS && isIndexSkipped(metadata);
}
